import {
  Button,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  UnorderedList,
  VStack,
} from "@chakra-ui/react";
import { KYDDashboardWaitlistBuyOrder } from "@common/interfaces/KYDTicket";
import { ErrorMessage } from "@hookform/error-message";
import { useContext, useEffect, useMemo, useState } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { KYDGuestlistDisplayState } from "@common/interfaces/KYDEvent";
import { createDialogContext, DialogProvider } from "../../../../Common/Dialog";
import { isTicketTypeSystem } from "../../../../utils";
import { AlgoliaAttendeeObject } from "@common/interfaces/Algolia";
import { AddIcon, MinusIcon } from "@chakra-ui/icons";
import { chakra } from "@chakra-ui/react";
import { MdMoney } from "react-icons/md";
import {
  IoCardOutline,
  IoMail,
  IoMailOutline,
  IoPersonOutline,
  IoTicketOutline,
} from "react-icons/io5";
import Dinero from "dinero.js";

const InlineText = chakra.span;

export const FulfillDialogContext = createDialogContext();

function groupAndSumByName(arr: Array<any>) {
  const grouped = arr.reduce((acc, curr) => {
    if (!acc[curr.name]) {
      acc[curr.name] = { name: curr.name, quantity: 0 };
    }
    acc[curr.name].quantity += 1;

    return acc;
  }, {});

  return Object.values(grouped) as { name: string; quantity: number }[];
}

export type FulfillDialogValues = {};

const FulfillDialog = ({
  onSave,
  isLoading = false,
  orders = [],
}: {
  isLoading: boolean;
  orders?: KYDDashboardWaitlistBuyOrder[];
  onSave: (data: KYDDashboardWaitlistBuyOrder[]) => void;
}) => {
  const { isOpen, close } = useContext(FulfillDialogContext);

  const _onSave = () => {
    return onSave(orders);
  };

  const onClose = () => {
    close();
  };

  const order = useMemo(() => {
    const total_without_fees = orders.reduce((total: Dinero.Dinero, order) => {
      return total.add(
        order.cart.line_items.reduce((liTotal: Dinero.Dinero, li) => {
          return liTotal.add(
            Dinero({
              amount: li.unit_subtotal.amount,
              currency: li.unit_subtotal.currency,
            })
          );
        }, Dinero({ amount: 0, currency: "USD" }))
      );
    }, Dinero({ amount: 0, currency: "USD" }));

    const formatter = new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    });
    const units = total_without_fees.toUnit();
    const display_total = formatter.format(units);

    const ticket_types = orders.reduce(
      (ticket_types, order) => {
        return ticket_types.concat(...order.cart.line_items);
      },
      []
    );

    return {
      display_total,
      number_of_customers: orders.length,
      ticket_types: groupAndSumByName(ticket_types),
    };
  }, [orders]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        isCentered
        closeOnOverlayClick={false}
      >
        <ModalOverlay />
        <ModalContent
          mb={[0, "auto"]}
          borderBottomLeftRadius={[0, "md"]}
          borderBottomRightRadius={[0, "md"]}
        >
          <ModalHeader paddingTop={4}>
            <VStack gap={0} width={"full"}>
              <Text w="full">Fill waitlist requests</Text>
            </VStack>
          </ModalHeader>
          <ModalBody textAlign={"left"}>
            <VStack spacing={2} w="full">
              <Text w="full">
                Are you sure you want to fill{" "}
                <InlineText fontWeight={"bold"}>{orders.length}</InlineText>{" "}
                waitlist request(s)?
              </Text>
              <Text w="full">
                Please read carefully all the implications below:
              </Text>
              <UnorderedList w="full" listStyleType={"none"} py={2} m={0}>
                <ListItem
                  p={2}
                  my={2}
                  w="full"
                  borderWidth={1}
                  borderColor={"gray.200"}
                  rounded="md"
                >
                  <ListIcon as={IoCardOutline} color="green.500" />A total of{" "}
                  <InlineText fontWeight={"bold"}>
                    {order?.display_total}
                  </InlineText>{" "}
                  face value of tickets will be sold
                </ListItem>
                {order?.ticket_types.map((li) => (
                  <ListItem
                    my={2}
                    p={2}
                    w="full"
                    borderWidth={1}
                    borderColor={"gray.200"}
                    rounded="md"
                  >
                    <ListIcon as={IoTicketOutline} color="green.500" />
                    The <InlineText fontWeight={"bold"}>
                      {li.name}
                    </InlineText>{" "}
                    ticket type quantity will be increased by{" "}
                    <InlineText fontWeight={"bold"}>{li.quantity}</InlineText>
                  </ListItem>
                ))}

                {order?.ticket_types.map((li) => (
                  <ListItem
                    my={2}
                    p={2}
                    w="full"
                    borderWidth={1}
                    borderColor={"gray.200"}
                    rounded="md"
                  >
                    <ListIcon as={IoPersonOutline} color="green.500" />
                    <InlineText fontWeight={"bold"}>
                      {li.quantity} x {li.name}
                    </InlineText>{" "}
                    tickets will be assigned to customers
                  </ListItem>
                ))}

                <ListItem
                  p={2}
                  my={2}
                  w="full"
                  borderWidth={1}
                  borderColor={"gray.200"}
                  rounded="md"
                >
                  <ListIcon as={IoMailOutline} color="green.500" />
                  <InlineText fontWeight={"bold"}>
                    {orders.length}
                  </InlineText>{" "}
                  customer(s) will{" "}
                  <InlineText fontWeight={"bold"}>receive an email</InlineText>{" "}
                  with their waitlist order fulfillment confirmation
                </ListItem>
              </UnorderedList>

              <InlineText w="full" fontWeight={"bold"}>
                This action can not be undone
              </InlineText>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Stack direction={["column-reverse", "row-reverse"]} width="100%">
              <Button
                key={"cancel"}
                isLoading={isLoading}
                isDisabled={isLoading}
                w={"auto"}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                key={"submit"}
                bg="green.500"
                color="white"
                w={"auto"}
                isLoading={isLoading}
                isDisabled={isLoading}
                onClick={(data) => _onSave()}
              >
                Fill Requests
              </Button>
            </Stack>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export const withFulfillDialog =
  <T extends object>(Component: React.ComponentType<T>) =>
  (props: T) => {
    return (
      <DialogProvider context={FulfillDialogContext}>
        <Component {...(props as T)} />
      </DialogProvider>
    );
  };

export default FulfillDialog;
